import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { comics, categories, heroImage } from '@/mocks/comics';
import { faqs } from '@/mocks/faqs';
import { usePageMeta } from '@/hooks/usePageMeta';
import ComicCard from '@/components/base/ComicCard';
import SectionHeading from '@/components/base/SectionHeading';
import FaqAccordion from '@/components/feature/FaqAccordion';
import BackTop from '@/components/base/BackTop';

const categoryColor: Record<string, string> = {
  fantasy: 'bg-primary-500',
  campus: 'bg-accent-500',
  romance: 'bg-secondary-500',
};

const categoryHover: Record<string, string> = {
  fantasy: 'hover:ring-primary-400',
  campus: 'hover:ring-accent-400',
  romance: 'hover:ring-secondary-400',
};

const features = [
  { icon: 'ri-timer-flash-line', title: '更新及时', desc: '连载作品持续跟进，首页与分类页实时标注最新更新时间。' },
  { icon: 'ri-grid-fill', title: '分类直达', desc: '奇幻、校园、恋爱题材清晰分区，点开即看，不用反复搜索。' },
  { icon: 'ri-focus-3-line', title: '题材聚合', desc: '热门题材一站式聚合，想看的韩漫类型一次找齐。' },
  { icon: 'ri-device-line', title: '多端适配', desc: '手机、平板、电脑多端流畅浏览，随时随地追更。' },
];

const stats = [
  { num: '15+', label: '精选题材作品' },
  { num: '30+', label: '细分题材标签' },
  { num: '3', label: '热门分类直达' },
  { num: '全天候', label: '持续更新' },
];

export default function Home() {
  usePageMeta({
    title: '韩漫漫画网 - 奇幻/校园/恋爱韩漫分类直达',
    description:
      '韩漫漫画网聚合奇幻、校园、恋爱等热门题材韩漫，分类清晰直达，点开即看。不用反复搜索，一站找齐你想看的韩漫类型，快速定位心头好。',
    keywords: '韩漫漫画网,韩漫网奇幻漫画,韩漫网校园漫画,韩漫网恋爱漫画,韩漫题材分类',
    canonical: 'https://lovecomix.github.io/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '韩漫漫画网首页',
        url: 'https://lovecomix.github.io/',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '韩漫漫画网精选作品',
        itemListElement: comics
          .filter((c) => c.isFeatured)
          .map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.title,
          })),
      },
    ],
  });

  const [query, setQuery] = useState('');
  const [input, setInput] = useState('');

  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return comics.filter(
      (c) => c.title.includes(q) || c.categoryName.includes(q) || c.tags.some((t) => t.includes(q)),
    );
  }, [query]);

  const featured = comics.filter((c) => c.isFeatured);

  const scrollToFeatured = () => {
    document.getElementById('featured')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative flex min-h-[560px] items-center overflow-hidden">
        <img
          src={heroImage}
          alt="韩漫漫画网热门题材聚合背景图"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/30" />
        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:py-32">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs text-background-100 backdrop-blur">
            <i className="ri-sparkling-2-line text-secondary-300" />
            奇幻 · 校园 · 恋爱 三大题材聚合直达
          </span>
          <h1 className="font-heading text-3xl leading-tight text-background-50 md:text-5xl md:leading-tight">
            韩漫漫画网
            <br className="hidden md:block" />
            一站找齐你想看的韩漫类型
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            不用反复搜索，分类清晰直达，点开即看。热门题材聚合，快速定位你的心头好。
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(input);
              scrollToFeatured();
            }}
            className="mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row sm:items-center sm:rounded-full sm:bg-background-50/95 sm:p-1.5 sm:backdrop-blur"
          >
            <label className="flex flex-1 items-center gap-2 rounded-full bg-background-50/95 px-4 py-3 sm:bg-transparent">
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索题材或作品名，如：龙、校园、暗恋"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </label>
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              立即查找
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-background-300">热门：</span>
            {categories.map((c) => (
              <Link
                key={c.key}
                to={`/${c.key}`}
                className="rounded-full bg-background-50/15 px-3.5 py-1.5 text-xs text-background-100 backdrop-blur transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="热门题材分类"
          title="选择你喜欢的韩漫题材"
          desc="奇幻、校园、恋爱三大分类直达，每个分类下还有更细的标签，帮你快速锁定想看的类型。"
        />
        <div className="grid gap-6 md:grid-cols-3">
          {categories.map((cat) => (
            <Link
              key={cat.key}
              to={`/${cat.key}`}
              className={`group relative overflow-hidden rounded-2xl bg-background-100 ring-1 ring-background-200/70 transition-all hover:-translate-y-1 ${categoryHover[cat.key]}`}
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden">
                <img
                  src={cat.cover}
                  alt={`${cat.name}分类直达入口`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span
                  className={`absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-background-50 ${categoryColor[cat.key]}`}
                >
                  {cat.key === 'fantasy' ? (
                    <i className="ri-sword-line" />
                  ) : cat.key === 'campus' ? (
                    <i className="ri-graduation-cap-line" />
                  ) : (
                    <i className="ri-heart-2-line" />
                  )}
                </span>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-semibold text-foreground-950">{cat.name}</h3>
                <p className="mt-1 text-xs text-foreground-600">{cat.tagline}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors group-hover:text-primary-500">
                  进入分类
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured + search */}
      <section id="featured" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow={query.trim() ? '搜索结果' : '编辑精选'}
            title={query.trim() ? `「${query.trim()}」的搜索结果` : '为你精选的热门韩漫'}
            desc={
              query.trim()
                ? `共找到 ${searchResults.length} 部相关作品，你也可以切换上方分类进一步筛选。`
                : '从奇幻到恋爱，编辑团队从海量题材里挑出的口碑之作，点开即看。'
            }
          />

          {query.trim() ? (
            searchResults.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {searchResults.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl bg-background-50 py-20 text-center">
                <i className="ri-search-eye-line text-4xl text-foreground-300" />
                <p className="mt-3 text-foreground-600">没有找到匹配的作品，换个关键词试试</p>
                <button
                  type="button"
                  onClick={() => {
                    setInput('');
                    setQuery('');
                  }}
                  className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600"
                >
                  清除搜索
                </button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
              {featured.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest updates */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <SectionHeading
            eyebrow="实时更新"
            title="最近更新"
            desc="连载中的热门作品持续跟进，第一时间掌握最新话更新。"
            align="left"
          />
          <Link
            to="/fantasy"
            className="mb-8 inline-flex shrink-0 items-center gap-1 rounded-full bg-background-100 px-4 py-2 text-sm text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            浏览全部 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-6">
          {comics.slice(0, 6).map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="站内看点"
            title="为什么选择韩漫漫画网"
            desc="围绕题材聚合与阅读体验打磨每个细节，让你把时间花在看漫画上，而不是找漫画上。"
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl bg-background-50 p-6 ring-1 ring-background-200/70 transition-all hover:-translate-y-1 hover:ring-accent-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-100 text-2xl text-accent-700">
                  <i className={f.icon} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="overflow-hidden rounded-2xl bg-primary-500">
          <div className="grid grid-cols-2 gap-px md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center px-4 py-10 text-center">
                <span className="font-heading text-3xl text-background-50 md:text-4xl">{s.num}</span>
                <span className="mt-2 text-xs text-background-200 md:text-sm">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="常见问题"
            title="你可能想问"
            desc="关于题材分类、更新与阅读的常见疑问，这里都有答案。"
          />
          <FaqAccordion items={faqs.slice(0, 4)} />
          <div className="mt-8 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 rounded-full bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-600 ring-1 ring-primary-200 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              查看全部常见问题 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="relative overflow-hidden rounded-2xl bg-accent-600 px-6 py-12 text-center md:px-12 md:py-16">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-accent-500/40 blur-2xl" />
          <div className="absolute -bottom-10 -left-8 h-40 w-40 rounded-full bg-secondary-400/30 blur-2xl" />
          <div className="relative">
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">今天想看哪部韩漫？</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-100">
              奇幻的冒险、校园的青春、恋爱的甜蜜，总有一款适合当下的你。选个题材，点开即看。
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/${cat.key}`}
                  className="rounded-full bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:bg-secondary-300"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BackTop />
    </div>
  );
}